/**
 * Wrapper element that groups the containers of a single multi-container
 * amendment point (e.g. an inserted group heading plus its paragraphs) so the
 * run reads and behaves as one changed section: one highlight box, one click
 * target, one selection outline.
 */
export const CONSOLIDATION_CHANGE_GROUP_CLASS = "consolidation-change-group";

/**
 * Wraps contiguous changed sibling containers that share the same amendment
 * point (`changeLawExternalId#changeLawPosition`) into a single group element,
 * returning the transformed HTML string.
 *
 * The wrapping is injected into the HTML *before* it is parsed into React
 * elements, so React owns the wrapper nodes. This is deliberate: reparenting
 * React-managed DOM nodes imperatively would corrupt reconciliation.
 *
 * Only genuinely adjacent same-parent element siblings are grouped, so scattered
 * changes from the same point (e.g. an "Overalt i loven" global replace) and
 * containers in different structural parents are left untouched.
 */
export const wrapGroupedChangedContainersHtml = (
  html: string,
  changedContainerIds: ReadonlySet<string>,
  groupKeys: ReadonlyMap<string, string>
): string => {
  if (
    !html ||
    changedContainerIds.size === 0 ||
    groupKeys.size === 0 ||
    typeof DOMParser === "undefined"
  ) {
    return html;
  }

  const doc = new DOMParser().parseFromString(
    `<div id="__cc_group_root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("__cc_group_root");
  if (!root) return html;

  const containers = Array.from(
    root.querySelectorAll<HTMLElement>("[data-container-type][id]")
  );

  const flushRun = (run: HTMLElement[]) => {
    if (run.length < 2) return;
    const [first] = run;
    const parent = first.parentNode;
    if (!parent) return;

    const wrapper = doc.createElement("div");
    wrapper.setAttribute("class", CONSOLIDATION_CHANGE_GROUP_CLASS);
    wrapper.setAttribute("data-consolidation-representative-id", first.id);
    wrapper.setAttribute(
      "data-consolidation-representative-type",
      first.getAttribute("data-container-type") ?? ""
    );
    parent.insertBefore(wrapper, first);
    run.forEach((el) => wrapper.appendChild(el));
  };

  let current: HTMLElement[] = [];
  let previousKey: string | null = null;

  for (const el of containers) {
    const isChanged = changedContainerIds.has(el.id);
    const key = isChanged ? groupKeys.get(el.id) ?? null : null;
    const last = current[current.length - 1];
    const continuesRun =
      key !== null &&
      last !== undefined &&
      key === previousKey &&
      last.parentNode === el.parentNode &&
      last.nextElementSibling === el;

    if (continuesRun) {
      current.push(el);
    } else {
      flushRun(current);
      current = key !== null ? [el] : [];
    }
    previousKey = key;
  }
  flushRun(current);

  return root.innerHTML;
};
